import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import { AnimatePresence, MotionConfig, motion } from "framer-motion";
import { API_URL, STRIPE_PUBLISHABLE_KEY } from "../../config";
import AgreementStep from "./AgreementStep";
import PaymentStep from "./PaymentStep";
import PriceStep from "./PriceStep";
import Confirmation from "./Confirmation";
import OnboardingDone from "./OnboardingDone";
import DeclineScreen from "./DeclineScreen";
import ConfirmDecline from "./ConfirmDecline";
import Terminal, { Notice } from "./Terminal";
import Stepper from "./Stepper";
import { Brand, Direction } from "./StepShell";
import { backButton } from "./ui";
import { errorCode, errorMessage } from "./apiError";
import { awaitingCarrier } from "./offerState";

const stripePromise = STRIPE_PUBLISHABLE_KEY
  ? loadStripe(STRIPE_PUBLISHABLE_KEY)
  : null;

function savedMethod(data) {
  return data.has_saved_method
    ? {
        payment_method_type: data.saved_method_type,
        last4: data.saved_method_last4,
        bank_name: data.saved_method_bank_name,
      }
    : null;
}

// Where a freshly opened link lands: the first step this carrier hasn't done.
function landing(data) {
  if (data.is_terminal) {
    // Flight Mode off declines open links; the carrier didn't, and no new
    // match is coming, so show the neutral inactive screen.
    const carrierDeclined =
      data.status === "declined" && data.declined_via !== "flight_mode_off";
    return { screen: carrierDeclined ? "declined" : "terminal" };
  }
  // Agreement and payment only, for a load Cinesis doesn't book.
  if (data.purpose === "onboarding") {
    if (data.onboarding_complete) return { screen: "done" };
    if (data.agreement_required) return { screen: "steps", step: 1 };
    if (!data.has_saved_method) return { screen: "steps", step: 2 };
    return { screen: "finishing" };
  }
  // A broker answering a price only happens after a reservation.
  if (awaitingCarrier(data)) {
    return { screen: "steps", step: 3, standalone: true };
  }
  if (data.status === "payment_method_saved") return { screen: "done" };
  if (data.allow_reserve === false && data.price_editable === false) {
    return { screen: "not_open" };
  }
  if (data.agreement_required) return { screen: "steps", step: 1 };
  if (!data.has_saved_method) return { screen: "steps", step: 2 };
  return { screen: "steps", step: 3, standalone: true };
}

export default function BookIt() {
  const { token } = useParams();
  const [booking, setBooking] = useState(null);
  const [screen, setScreen] = useState("loading");
  const [step, setStep] = useState(1);
  const [standalone, setStandalone] = useState(false);
  const [saved, setSaved] = useState(null);
  const [result, setResult] = useState(null);
  const [declineFrom, setDeclineFrom] = useState(null);
  const [dir, setDir] = useState(1);
  const [confirmingDecline, setConfirmingDecline] = useState(false);
  const [agreed, setAgreed] = useState(false);
  // Why the server sent the carrier back to a step.
  const [stepNotice, setStepNotice] = useState("");
  const [error, setError] = useState("");

  const tokenUrl = useMemo(() => `${API_URL}/book-it/${token}`, [token]);

  // Ends an onboarding link; the agent texts the next step from here. A
  // refusal for a missing piece sends the carrier back to that step.
  const finish = useCallback(async () => {
    try {
      await axios.post(`${tokenUrl}/onboarding/complete`);
      setDir(1);
      setScreen("done");
    } catch (err) {
      const message = errorMessage(err, "We couldn't finish setting you up.");
      const back = {
        AGREEMENT_REQUIRED: 1,
        PAYMENT_METHOD_REQUIRED: 2,
      }[errorCode(err)];
      if (!back) {
        setError(message);
        setScreen("error");
        return;
      }
      if (back === 1) setAgreed(false);
      else setSaved(null);
      setDir(-1);
      setStandalone(false);
      setStep(back);
      setStepNotice(message);
      setScreen("steps");
    }
  }, [tokenUrl]);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(tokenUrl)
      .then(({ data }) => {
        if (cancelled) return;
        const where = landing(data);
        setBooking(data);
        setSaved(savedMethod(data));
        setAgreed(!data.agreement_required);
        // Finishing stays on the loading screen: swapping it for another
        // screen mid-request can strand the slide-out on a fast reply.
        setScreen(where.screen === "finishing" ? "loading" : where.screen);
        setStep(where.step || 1);
        setStandalone(Boolean(where.standalone));
        if (where.screen === "finishing") finish();
      })
      .catch((err) => {
        if (cancelled) return;
        setError(
          errorMessage(err, "We couldn't open this link. It may have expired."),
        );
        setScreen("error");
      });
    return () => {
      cancelled = true;
    };
  }, [tokenUrl, finish]);

  function goTo(n) {
    setStepNotice("");
    setDir(screen !== "steps" || n >= step ? 1 : -1);
    setStandalone(false);
    setStep(n);
    setScreen("steps");
  }

  function show(next, direction = 1) {
    setDir(direction);
    setScreen(next);
  }

  // Errors stay on the decline screen, which shows them.
  async function decline(reason) {
    await axios.post(`${tokenUrl}/decline`, { reason });
    show("declined");
  }

  async function refresh() {
    try {
      const { data } = await axios.get(tokenUrl);
      setBooking(data);
      return data;
    } catch {
      return null;
    }
  }

  // "Not interested" asks first; the reason screen only follows a yes.
  const startDecline = () => setConfirmingDecline(true);
  const keepLoad = useCallback(() => setConfirmingDecline(false), []);
  const confirmDecline = () => {
    setConfirmingDecline(false);
    setDeclineFrom(screen);
    show("decline");
  };

  const onboarding = booking?.purpose === "onboarding";

  let body = null;
  if (screen === "loading") {
    body = <Notice title="Loading…" />;
  } else if (screen === "error") {
    body = <Notice title="Something's not right" body={error} />;
  } else if (screen === "terminal") {
    body = <Terminal status={booking?.status} />;
  } else if (screen === "declined") {
    body = (
      <Notice
        title="Got it"
        body="Got it — we'll keep looking and send you another match."
      />
    );
  } else if (screen === "decline") {
    body = <DeclineScreen onSend={decline} />;
  } else if (screen === "not_open") {
    body = (
      <Notice
        title="This load isn't open right now"
        body="We'll text you when it is, or send you another match."
        onDecline={startDecline}
      />
    );
  } else if (screen === "done") {
    body = onboarding ? (
      <OnboardingDone source={booking.onboarding_source} token={token} />
    ) : (
      <Confirmation booking={booking} result={result} token={token} />
    );
  } else if (step === 1) {
    body = (
      <AgreementStep
        tokenUrl={tokenUrl}
        alreadyAgreed={agreed}
        notice={stepNotice}
        next={onboarding && saved ? "finish" : saved ? "offer" : "payment"}
        onDecline={startDecline}
        onDone={() => {
          setAgreed(true);
          if (onboarding && saved) finish();
          else goTo(saved && !onboarding ? 3 : 2);
        }}
      />
    );
  } else if (step === 2) {
    body = stripePromise ? (
      <Elements stripe={stripePromise}>
        <PaymentStep
          tokenUrl={tokenUrl}
          truckerName={booking.trucker_name}
          saved={saved}
          freeLoad={!onboarding && Boolean(booking.free_booking_available)}
          notice={stepNotice}
          onSaved={setSaved}
          onDone={() => (onboarding ? finish() : goTo(3))}
          onDecline={startDecline}
        />
      </Elements>
    ) : (
      <Notice
        title="Payments aren't set up"
        body="Stripe is not configured. Please contact support."
      />
    );
  } else {
    body = (
      <PriceStep
        booking={booking}
        tokenUrl={tokenUrl}
        onDecline={startDecline}
        onNeedStep={goTo}
        onRefresh={refresh}
        onSubmitted={(r) => {
          setResult(r);
          show("done");
        }}
      />
    );
  }

  const inSteps = screen === "steps";
  const showStepper = inSteps && !standalone;
  const back =
    screen === "decline"
      ? () => show(declineFrom, -1)
      : showStepper && step > 1
        ? () => goTo(step - 1)
        : null;

  return (
    <MotionConfig reducedMotion="user">
      <div className="flex min-h-[100dvh] flex-col items-center bg-slate-50 font-[Roboto] text-slate-900 antialiased">
        {/* Stays put while the screen below slides, so the stepper can animate. */}
        <header className="flex w-full max-w-[480px] flex-col gap-2.5 px-5 pt-2.5">
          <div className="flex min-h-8 items-center">
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={back ? "back" : "brand"}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.18 }}
              >
                {back ? (
                  <button type="button" onClick={back} className={backButton}>
                    ‹ Back
                  </button>
                ) : (
                  <Brand />
                )}
              </motion.div>
            </AnimatePresence>
          </div>
          <AnimatePresence initial={false}>
            {showStepper && (
              <motion.div
                key="stepper"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              >
                <Stepper
                  current={step}
                  steps={onboarding ? ["Agreement", "Payment"] : undefined}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </header>

        <Direction.Provider value={dir}>
          <AnimatePresence mode="wait" custom={dir} initial={false}>
            <motion.div
              key={inSteps ? `step-${step}` : screen}
              initial="enter"
              animate="center"
              exit="exit"
              className="flex w-full max-w-[480px] flex-1 flex-col"
            >
              {body}
            </motion.div>
          </AnimatePresence>
        </Direction.Provider>

        <ConfirmDecline
          open={confirmingDecline}
          onKeep={keepLoad}
          onConfirm={confirmDecline}
        />
      </div>
    </MotionConfig>
  );
}
